import { useUserInfo } from '@/hooks/useUserInfo';
import { JobDetails } from '@/reducers/jbpGeneralSlice';
import { useAppSelector } from '@/store';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import * as Yup from 'yup';
import { FormDropdown } from '@portal_components/components/FormDropdown';
import { FormTagGroup } from '@portal_components/components/FormTagGroup';
import { FormRadioButtonGroup } from '@portal_components/components/FormRadioButtonGroup';
import { FormWorkflowView } from '@portal_components/formWorkflow/FormWorkflowView';
import { jobCommentsNode, siteAddressNode, siteContactDetailsNode } from '../GenericNodes';
import { Title } from '@portal_components/components/Title';

interface FormBookJobDnoBnoSiteInfoProps {
  mpan: string;
  handleSubmit: (values: JobDetails) => void;
}

export const FormBookJobDnoBnoSiteInfo = ({ mpan, handleSubmit }: FormBookJobDnoBnoSiteInfoProps) => {
  const { canAccess } = useUserInfo();
  const canAccessExtraSkills = canAccess('canSelectExtraSkills');
  const { site } = useAppSelector(state => state.jbp);
  const { dnoBnoDropdowns } = useAppSelector(state => state.standingData);

  if (!site || !dnoBnoDropdowns) return null;
  return (
    <div className="ml-10 pb-4 mb-10">
      <Title className="mt-7 mb-5" text={`Booking for: ${mpan}`} />
      <FormWorkflowView<JobDetails>
        onSubmit={handleSubmit}
        initialValues={{
          siteContactDetails: site.siteContactDetails,
          siteAddress: site.siteAddress
        }}
        nodes={[
          siteContactDetailsNode,
          siteAddressNode,
          {
            name: 'dnoBnoSiteInfo',
            element: (
              <div>
                <Title text="Please enter DNO/BNO Specific information required for this job booking" className="mb-5 mt-7" />
                <div className="w-[30em]">
                  <FormTextInput name="dnoBnoSiteInfo.exportCapacity" title="Export Capacity" placeholder="Please enter an Export Capacity value" mandatory />
                  <FormTextInput name="dnoBnoSiteInfo.importCapacity" title="Import Capacity" placeholder="Please enter an Import Capacity value" mandatory />
                  <FormDropdown
                    name="dnoBnoSiteInfo.serviceVoltage"
                    title="Service Voltage"
                    placeholder="Please select a Service Voltage"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.serviceVoltage.map(voltage => ({ title: voltage, value: voltage }))}
                  />
                  <FormDropdown
                    name="dnoBnoSiteInfo.meterPointVoltage"
                    title="Meter Point Voltage"
                    placeholder="Please select a Meter Point Voltage"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.mtrPointVoltage.map(voltage => ({ title: voltage, value: voltage }))}
                  />
                  <FormRadioButtonGroup
                    name="dnoBnoSiteInfo.hvMetering"
                    title="HV Metering"
                    buttons={[
                      { title: 'Yes', value: 'Yes' },
                      { title: 'No', value: 'No' }
                    ]}
                    mandatory
                  />
                  <FormDropdown
                    name="dnoBnoSiteInfo.ctRatio"
                    title="CT Ratio"
                    placeholder="Please select a CT Ratio"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.ctRatio.map(ratio => ({ title: ratio, value: ratio }))}
                  />
                  <FormDropdown
                    name="dnoBnoSiteInfo.vtRatio"
                    title="VT Ratio"
                    placeholder="Please select a VT Ratio"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.vtRatio.map(ratio => ({ title: ratio, value: ratio }))}
                  />
                  <FormDropdown
                    name="dnoBnoSiteInfo.serviceRating"
                    title="Service Rating"
                    placeholder="Please select a Service Rating"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.serviceRating.map(rating => ({ title: rating, value: rating }))}
                  />
                  <FormDropdown
                    name="dnoBnoSiteInfo.fuseSize"
                    title="Fuse Size"
                    placeholder="Please select a Fuse Size"
                    mandatory
                    elementsForDisplay={dnoBnoDropdowns.fuseSize.map(size => ({ title: size, value: size }))}
                  />
                  <FormRadioButtonGroup
                    name="dnoBnoSiteInfo.customerOwnedSwitch"
                    title="Customer Owned Switch"
                    buttons={[
                      { title: 'Yes', value: 'Yes' },
                      { title: 'No', value: 'No' },
                      { title: 'Unknown', value: 'Unknown' }
                    ]}
                    mandatory
                  />
                </div>
              </div>
            ),
            validationSchema: {
              dnoBnoSiteInfo: Yup.object({
                exportCapacity: Yup.string().required('Please enter an Export Capacity value').max(50),
                importCapacity: Yup.string().required('Please enter an Import Capacity value').max(50),
                serviceVoltage: Yup.string().required('Please select a Service Voltage'),
                meterPointVoltage: Yup.string().required('Please select a Meter Point Voltage'),
                hvMetering: Yup.string().required('Please select if there is HV Metering'),
                ctRatio: Yup.string().required('Please select a CT Ratio'),
                vtRatio: Yup.string().required('Please select a VT Ratio'),
                serviceRating: Yup.string().required('Please select a Service Rating'),
                fuseSize: Yup.string().required('Please select a Fuse Size'),
                customerOwnedSwitch: Yup.string().required('Please select if there is a Customer Owned Switch')
              }).required()
            }
          },
          {
            element: canAccessExtraSkills ? (
              <FormTagGroup
                title="Please select any required extra skills"
                name="extraSkills"
                classname="mr-10"
                options={[
                  {
                    title: '4x4',
                    value: '4x4'
                  }
                ]}
              />
            ) : null
          },
          jobCommentsNode
        ]}
      />
    </div>
  );
};
